import axios from "axios";
import { API } from "../config";

// withCredentials: send the HttpOnly auth cookie on every cross-origin request.
const apiClient = axios.create({ baseURL: API, withCredentials: true, timeout: 10000 });

// CSRF double-submit: the backend returns the CSRF token in the login/me response
// body; we store it in sessionStorage (see App.js) and echo it in the X-CSRF-Token
// header on mutating requests. Reading from the body (not the cookie) avoids the
// cross-subdomain cookie-readability problem (api.kzool.com cookie is invisible to
// app.kzool.com JS). Safe methods don't need the header.
const SAFE_METHODS = new Set(["get", "head", "options"]);

apiClient.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = sessionStorage.getItem("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// Endpoints where a 401 is expected/handled by the caller, NOT a mid-session
// expiry — so they must NOT trigger the global logout/redirect:
//  - login/register/etc.: a 401 means "wrong credentials" (shown inline);
//  - auth/me: the bootstrap "who am I?" probe — a 401 just means "no session"
//    (logged out). Redirecting here would reload → re-probe → 401 → infinite loop.
const NO_REDIRECT_ON_401_RE = /\/(auth\/login|auth\/register|auth\/forgot-password|auth\/reset-password|auth\/me|kzool-admin\/login|kzool-admin\/me)\b/;

// Handle 401 globally (expired / invalid session cookie) → redirect to login.
// The server owns the cookie, so there's no client token to clear; drop cached
// display data and redirect. Skipped for the endpoints above.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || "";
    if (error.response?.status === 401 && !NO_REDIRECT_ON_401_RE.test(url)) {
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("isKzoolAdmin");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export default apiClient;
